import React from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import api from '../api';
import { useNavigate, useLocation } from 'react-router-dom';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import Alert from '@mui/material/Alert';
import MenuIcon from '@mui/icons-material/Menu';
import FeedIcon from '@mui/icons-material/Feed';
import Groups2Icon from '@mui/icons-material/Groups2';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import LogoutIcon from '@mui/icons-material/Logout';
import QuizIcon from '@mui/icons-material/Quiz';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { sendSupportTicket } from '../api/support';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

export default function Header() {
  const theme = useTheme();
  const location = useLocation();
  const [role, setRole] = React.useState(null);
  const [openProfile, setOpenProfile] = React.useState(false);
  const [firstName, setFirstName] = React.useState('');
  const [schoolName, setSchoolName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [currentPassword, setCurrentPassword] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [snackbar, setSnackbar] = React.useState({ open: false, severity: 'success', message: '' });
  const [anchorEl, setAnchorEl] = React.useState(null);
  const navigate = useNavigate();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  // Support modal state
  const [openSupport, setOpenSupport] = React.useState(false);
  const [supportReason, setSupportReason] = React.useState('');
  const [supportLoading, setSupportLoading] = React.useState(false);
  const [supportType, setSupportType] = React.useState('');

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  // Hide the welcome text and hamburger when the user is unauthenticated
  // and on certain public pages (FAQ, Signup) where those controls are not appropriate.
  const hideWelcomeAndMenu = !token && (location.pathname === '/faq' || location.pathname.startsWith('/signup'));

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    navigate('/login');
  };

  const handleOpenSupport = () => {
    setSupportReason('');
    setSupportType('');
    setSnackbar({ open: false, severity: 'success', message: '' });
    setOpenSupport(true);
  };
  const handleCloseSupport = () => {
    setOpenSupport(false);
    setSupportReason('');
  };
  const handleSubmitSupport = async () => {
    setSupportLoading(true);
    try {
      await sendSupportTicket({
        firstName,
        lastName,
        schoolName,
        email,
        supportType,
        additionalInformation: supportReason,
      });
      setSnackbar({ open: true, severity: 'success', message: 'Support request sent!' });
      setTimeout(() => {
        setSnackbar({ open: false, severity: 'success', message: '' });
        setOpenSupport(false);
        setSupportReason('');
      }, 2000);
    } catch (err) {
      setSnackbar({ open: true, severity: 'error', message: err?.response?.data?.message || 'Failed to send support request' });
    } finally {
      setSupportLoading(false);
    }
  };

  const headerRef = React.useRef(null);
  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.get('/api/public/me');
        if (mounted && res?.data?.authenticated) {
          setRole(res.data.role || null);
          // cache role for quick checks
          if (res.data.role) localStorage.setItem('role', res.data.role);
          // prefill name fields
          if (res.data.firstName) setFirstName(res.data.firstName);
          if (res.data.lastName) setLastName(res.data.lastName);
          if (res.data.schoolName) setSchoolName(res.data.schoolName);
          if (res.data.email) setEmail(res.data.email);
        }
      } catch (err) {
        // ignore
      }
    })();
    return () => { mounted = false; };
  }, []);

  React.useLayoutEffect(() => {
    if (headerRef.current) {
    }
  });

  const handleOpenProfile = () => setOpenProfile(true);
  const handleCloseProfile = () => {
    setOpenProfile(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleSaveProfile = async () => {
    try {
      const payload = { firstName: firstName || '', lastName: lastName || '' };
      await api.post('/api/public/profile/update', payload);
      setSnackbar({ open: true, severity: 'success', message: 'Profile updated' });
      setRole(localStorage.getItem('role') || role);
      // show success inside modal briefly, then close modal
      setTimeout(() => {
        setSnackbar(s => ({ ...s, open: false }));
        handleCloseProfile();
      }, 1500);
    } catch (err) {
      setSnackbar({ open: true, severity: 'error', message: err?.response?.data?.message || 'Unable to update profile' });
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      setSnackbar({ open: true, severity: 'error', message: 'New passwords do not match' });
      return;
    }
    try {
      await api.post('/api/auth/change-password', { currentPassword, newPassword });
      setSnackbar({ open: true, severity: 'success', message: 'Password changed' });
      // show success inside modal briefly, then close modal
      setTimeout(() => {
        setSnackbar(s => ({ ...s, open: false }));
        handleCloseProfile();
      }, 1500);
    } catch (err) {
      setSnackbar({ open: true, severity: 'error', message: err?.response?.data?.message || 'Unable to change password' });
    }
  };

  return (
    <AppBar ref={headerRef} position="sticky" elevation={4}>
      <Toolbar sx={{ justifyContent: 'space-between', position: 'relative', ...(isSmall ? { flexDirection: 'column', alignItems: 'stretch', py: 1 } : {}) }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pr: 2, zIndex: 1, ...(isSmall ? { alignSelf: 'flex-start', ml: 2 } : {}) }}>
          <Box component="img" src="/ryslogo.png" alt="Home Depot" sx={{ height: { xs: 56, sm: 75 }, py: 1, objectFit: 'contain' }} />
        </Box>

        {!hideWelcomeAndMenu && (
          <Box sx={{ position: isSmall ? 'static' : 'absolute', left: isSmall ? 'auto' : 0, right: isSmall ? 'auto' : 0, display: 'flex', justifyContent: 'center', pointerEvents: 'none', width: '100%', mt: isSmall ? 1 : 0 }}>
            <Typography variant={isSmall ? 'h6' : 'h4'} sx={{ fontWeight: 700, color: '#fff', pointerEvents: 'auto', fontSize: { xs: '1.1rem', sm: '2rem' }, textAlign: 'center', whiteSpace: 'normal', wordBreak: 'break-word' }}>
              Welcome - {schoolName || ''}
            </Typography>
          </Box>
        )}

        {!hideWelcomeAndMenu && (
          <Box sx={isSmall ? { display: 'flex', alignItems: 'center', position: 'absolute', right: 8, top: 8 } : { display: 'flex', alignItems: 'center' }}>
            <Button
              onClick={handleMenu}
              aria-label="menu"
              sx={{
                minWidth: 0,
                mr: 0.5,
                px: 1,
                py: 0.5,
                color: '#fff',
                backgroundColor: 'rgba(0, 0, 0, 0.25)',
                fontWeight: 700,
                fontSize: '0.75rem',
                lineHeight: 1,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.35)',
                },
              }}
            >
              MENU
            </Button>
            <IconButton size="large" edge="end" color="inherit" onClick={handleMenu} aria-label="menu" sx={{ '& .MuiSvgIcon-root': { fontSize: 40 } }}>
              <MenuIcon />
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
              <MenuItem onClick={() => { handleClose(); navigate('/grantlanding'); }}>
                <ListItemIcon>
                  <FeedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Grants</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => { handleClose(); navigate('/faq'); }}>
                <ListItemIcon>
                  <QuizIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>FAQ</ListItemText>
              </MenuItem>
              {/* Help Docs menu item - commented out, restore when ready
              <MenuItem onClick={() => { handleClose(); navigate('/help'); }}>
                <ListItemIcon>
                  // <LiveHelpIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Help Docs</ListItemText>
              </MenuItem>
              */}
              {/* Rules removed */}
              {role === 'ADMIN' && (
                <MenuItem onClick={() => { handleClose(); navigate('/invitecollab'); }}>
                  <ListItemIcon>
                    <Groups2Icon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Invite Team Members</ListItemText>
                </MenuItem>
              )}

              <MenuItem onClick={() => { handleClose(); handleOpenProfile(); }}>
                <ListItemIcon>
                  <ManageAccountsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Profile</ListItemText>
              </MenuItem>

              <MenuItem onClick={() => { handleClose(); handleOpenSupport(); }}>
                <ListItemIcon>
                  <HelpOutlineIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Support</ListItemText>
              </MenuItem>

              <MenuItem onClick={() => { handleClose(); handleLogout(); }}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Logout</ListItemText>
              </MenuItem>
            </Menu>
            <Dialog open={openProfile} onClose={handleCloseProfile} fullWidth maxWidth="sm">
              <DialogTitle>Profile</DialogTitle>
              <DialogContent>
                {snackbar.open && (
                  <Alert severity={snackbar.severity} sx={{ mb: 2 }}>
                    {snackbar.message}
                  </Alert>
                )}
                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  <TextField label="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} fullWidth />
                  <TextField label="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} fullWidth />
                </Box>
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1">Change password</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                    <TextField label="Current password" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} fullWidth />
                    <TextField label="New password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} fullWidth />
                    <TextField label="Confirm new password" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} fullWidth />
                  </Box>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseProfile}>Cancel</Button>
                <Button onClick={handleSaveProfile}>Save Name</Button>
                <Button onClick={handleChangePassword} variant="contained">Change Password</Button>
              </DialogActions>
            </Dialog>
            <Dialog open={openSupport} onClose={handleCloseSupport} fullWidth maxWidth="sm">
              <DialogTitle>Support Request</DialogTitle>
              <DialogContent>
                {snackbar.open && (
                  <Alert severity={snackbar.severity} sx={{ mb: 2 }}>
                    {snackbar.message}
                  </Alert>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                  <TextField label="First name" value={firstName} fullWidth disabled required InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: '#f57c00' } } }} />
                  <TextField label="Last name" value={lastName} fullWidth disabled required InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: '#f57c00' } } }} />
                  <TextField label="School name" value={schoolName} fullWidth disabled required InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: '#f57c00' } } }} />
                  <TextField label="Email" value={email} fullWidth disabled required InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: '#f57c00' } } }} />
                  <FormControl fullWidth required>
                    <InputLabel id="support-type-label" sx={{ '& .MuiFormLabel-asterisk': { color: '#f57c00' } }}>Choose your type of support</InputLabel>
                    <Select labelId="support-type-label" value={supportType} label={"Choose your type of support"} onChange={(e) => setSupportType(e.target.value)}>
                      <MenuItem value={"My school is not eligible"}>My school is not eligible</MenuItem>
                      <MenuItem value={"I'm having trouble with my application"}>I'm having trouble with my application</MenuItem>
                      <MenuItem value={"I'm having trouble inviting a collaborator"}>I'm having trouble inviting a collaborator</MenuItem>
                      <MenuItem value={"I didn't receive my email verification"}>I didn't receive my email verification</MenuItem>
                      <MenuItem value={"Other"}>Other</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField label="Additional information" value={supportReason} onChange={e => setSupportReason(e.target.value)} fullWidth multiline minRows={3} required InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: '#f57c00' } } }} />
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseSupport} disabled={supportLoading}>Cancel</Button>
                <Button onClick={handleSubmitSupport} variant="contained" disabled={supportLoading || !supportReason.trim() || !supportType}>
                  {supportLoading ? 'Sending...' : 'Send'}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
}
